import { useState, useMemo, useCallback, type FocusEvent, useRef } from 'react';

import moment from 'moment';
import { useIntl } from 'react-intl';

import size from 'lodash/size';
import sortBy from 'lodash/sortBy';

import {
  DateField,
  PickersDay,
  MobileDatePicker,
  type PickersDayProps,
} from '@mui/x-date-pickers';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';

import { toMoment, toString } from 'src/utils/datetime';

import type { Moment } from 'moment';
import type { AxiosResponse } from 'axios';
import type { SxProps } from '@mui/material/styles';
import type { RequestSimplifiedData } from 'src/types/requestData';
import type { DateFieldSlotProps } from '@mui/x-date-pickers/DateField/DateField.types';

import messages from './messages';
import { Styled } from './index.styled';

const MIN_DATE = '1900-01-01';

// added '!' because of picker types- minDate will be always defined
const minDate = toMoment(MIN_DATE, moment.HTML5_FMT.DATE)!;

type DatePickerFieldProps = {
  name?: string;
  label?: string;
  error?: boolean;
  multiple?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  helperText?: string;
  value?: Moment | null;
  allowTyping?: boolean;
  allowPicker?: boolean;
  showIcon?: boolean;
  variant?: 'standard' | 'filled' | 'outlined';
  onChange?: (date: Moment | Moment[] | null) => void;
  onBlurChange?: (
    date: Moment | null,
  ) => Promise<void> | Promise<AxiosResponse<RequestSimplifiedData, any>>; //eslint-disable-line
  sx?: SxProps;
};

export const DatePickerField = ({
  sx,
  name,
  label,
  value,
  error,
  onChange,
  helperText,
  onBlurChange,
  allowTyping,
  allowPicker,
  showIcon = true,
  readOnly = false,
  multiple = false,
  disabled = false,
  variant = 'standard',
}: DatePickerFieldProps) => {
  const { formatMessage } = useIntl();

  const originalValue = useRef(value);
  const [date, setDate] = useState(value);
  const [open, setOpen] = useState(false);
  const [selectedDates, setSelectedDates] = useState<Moment[]>([]);
  const [dateError, setDateError] = useState<string | null>(null);

  // Handle the label for multiple-select
  const dateFieldLabel = useMemo(() => {
    const selectedDatesLength = size(selectedDates);
    if (selectedDatesLength === 0) return label;
    return selectedDatesLength === 1
      ? `${selectedDatesLength} ${formatMessage(messages.dayLabel)}`
      : `${selectedDatesLength} ${formatMessage(messages.daysLabel)}`;
  }, [label, selectedDates]);

  const preventEventBubbling = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  // Handle day click for multiple mode
  const handleDayClick = useCallback(
    (day: Moment) => {
      setSelectedDates((prevSelectedDates) => {
        const isSelected = prevSelectedDates.some((selected) =>
          day.isSame(selected, 'day'),
        );
        const newSelectedDates = isSelected
          ? prevSelectedDates.filter((selected) => !day.isSame(selected, 'day')) // Remove if already selected
          : [...prevSelectedDates, day]; // Add if not selected

        const sortedDates = sortBy(newSelectedDates, (date) => +date);
        onChange?.(sortedDates);
        return sortedDates;
      });
    },
    [onChange],
  );

  // Render the individual day in the calendar for multiple mode
  const handleRenderDay = useCallback(
    (props: PickersDayProps<Moment>) => {
      const isSelectedDay = selectedDates.some((selectedDate) =>
        selectedDate.isSame(props.day, 'day'),
      );
      return (
        <PickersDay
          {...props}
          selected={isSelectedDay}
          onDaySelect={() => handleDayClick(props.day)}
        />
      );
    },
    [selectedDates, handleDayClick],
  );

  const handleChange = useCallback(
    (date: Moment | null) => {
      if (multiple) return;
      if (date && date.isBefore(minDate)) {
        setDateError(
          formatMessage(messages.minDateError, {
            minDate: toString(minDate),
          }),
        );
      } else {
        setDateError(null);
      }
      onChange?.(date);
    },
    [onChange, multiple],
  );

  const dateFieldSlotProps = useMemo(() => {
    return {
      textField: {
        fullWidth: true,
        label: dateFieldLabel,
        variant,
        error: error || !!dateError,
        helperText: dateError || helperText,
        ...(showIcon
          ? {
              InputProps: {
                endAdornment: (
                  <TodayOutlinedIcon onClick={handleOpen} color="primary" />
                ),
              },
            }
          : {}),
      },
    } as DateFieldSlotProps<Moment, boolean>;
  }, [showIcon, error, helperText, dateFieldLabel, variant, dateError]);

  const dateFieldProps = useMemo(
    () => ({
      sx,
      name,
      value,
      error: error || !!dateError,
      disabled,
      readOnly,
      helperText: dateError || helperText,
      minDate,
      slotProps: dateFieldSlotProps,
    }),
    [
      sx,
      name,
      value,
      error,
      disabled,
      readOnly,
      helperText,
      dateFieldSlotProps,
      dateError,
    ],
  );

  const mobilePickerProps = useMemo(() => {
    if (!allowTyping) return dateFieldProps;
    return {
      ...dateFieldProps,
      open,
      onClose: handleClose,
      onAccept: handleClose,
    };
  }, [allowTyping, dateFieldProps, open]);

  const handleBlur = useCallback(
    (_: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const originalDate = originalValue.current;

      // If date is null, just clear it without error
      if (!date) {
        setDateError(null);
        if (onBlurChange && originalDate) {
          onBlurChange(null)
            .then(() => {
              originalValue.current = null;
            })
            .catch(() => undefined);
        }
        return;
      }

      // Early return if no change handler or invalid date
      if (!onBlurChange || !date.isValid()) {
        setDateError(formatMessage(messages.invalidDate));
        return;
      }

      // Validate against minimum date
      if (date.isBefore(minDate)) {
        setDateError(
          formatMessage(messages.minDateError, {
            minDate: toString(minDate),
          }),
        );
        return;
      }

      // Case 1: No original date, but we have a new valid date
      if (!originalDate) {
        onBlurChange(date)
          .then(() => {
            originalValue.current = date;
            setDateError(null);
          })
          .catch(() => undefined);
        return;
      }

      // Case 2: Both dates exist, check if they're different
      if (date.isValid()) {
        const datesAreDifferent = !originalDate
          .startOf('day')
          .isSame(date.startOf('day'), 'day');
        if (datesAreDifferent) {
          onBlurChange(date)
            .then(() => {
              originalValue.current = date;
              setDateError(null);
            })
            .catch(() => undefined);
        }
      }
    },
    [onBlurChange, date],
  );

  const renderDateField = useCallback(
    () => (
      <DateField
        {...dateFieldProps}
        fullWidth
        variant={variant}
        onChange={setDate}
        label={dateFieldLabel}
        onBlur={handleBlur}
      />
    ),
    [dateFieldProps, variant, handleBlur, dateFieldLabel],
  );

  return (
    <>
      {allowTyping && renderDateField()}

      {allowPicker && (
        <Styled.Wrapper allowTyping={allowTyping}>
          <MobileDatePicker
            {...mobilePickerProps}
            reduceAnimations
            onChange={handleChange}
            slots={multiple ? { day: handleRenderDay } : undefined}
          />
        </Styled.Wrapper>
      )}

      {!allowTyping && !allowPicker && (
        <div onClick={preventEventBubbling}>{renderDateField()}</div>
      )}
    </>
  );
};
